/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {useAppDispatch, useAppSelector} from "../store";
import {Button, Drawer, Dropdown, Form, Input, Menu, Modal, Select, Space} from "antd";
import {useEffect, useState} from "react";
import {closeDrawer} from "../reducers/drawers-reducer";
import {triggerReload} from "../reducers/reloadsignal-reducer";
import {del, patch, post} from "../core/request";
import {IconSet} from "../components/icons";
import {CustomButton} from "../components/buttons";

import {Person} from "../core/datamodel";
import {useHistory} from "react-router-dom";
import TextArea from "antd/es/input/TextArea";
import {VCFImport} from "../components/vcfImport";

type Class = Person
const stack_selector = (state: any) => state.drawer.person
const uri = "persons"
const key = "person"
const title = "Kontakt"

interface DrawerProps {
    size?: 'default' | 'large'
}


export function PersonDrawer(props: DrawerProps) {
    const history = useHistory();
    const stack = useAppSelector(stack_selector)
    const drawer_stack = useAppSelector(state => state.drawer.stack)
    const dispatch = useAppDispatch();
    const [object, setObject] = useState({} as Class);
    const [form] = Form.useForm();

    const isNew = () => !(!!object?.props?.uuid)

    useEffect(() => {
        const object = stack[stack.length - 1] as Class
        form.resetFields()
        form.setFieldsValue(object?.props)
        setObject(object)
    }, [stack, form])

    const onFormFinish = () => {
        object.update(form.getFieldsValue())

        let req: JQueryPromise<any>;
        if (isNew()) {
            req = post(uri, object.dump())
        } else {
            req = patch(uri + "/" + object.props.uuid, object.dump())
        }

        req.then(() => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDisable = () => {
        object.update({'disabled': !object.props.disabled})
        patch(uri + "/" + object.props.uuid,
            {
                'uuid': object.props.uuid,
                'disabled': object.props.disabled
            }
        ).then(
            () => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDelete = () => {
        Modal.confirm({
            title: "Eintrag wirklich gelöscht werden?",
            content: "Diese Aktion kann nicht rückgängig gemacht werden!",
            maskClosable: true,
            cancelText: 'Abbrechen',
            autoFocusButton: 'cancel',
            okText: 'Löschen',
            okType: 'danger',
            onOk: () => {
                del(uri + "/" + object.props.uuid)
                    .then(
                        () => {
                            history.goBack()
                            dispatch(triggerReload(key))
                            dispatch(closeDrawer(key))
                        },
                        (e) => {
                            console.error(e);
                        })
                return false;
            }
        })
    }

    const onDrawerClose = (e: any) => {
        if (e.type === 'click' &&
            (e.target as Element).className === "ant-drawer-mask"
        ) {
            Modal.confirm({
                title: "Fenster wirklich schließen?",
                content: "Alle Änderungen gehen verloren!",
                maskClosable: true,
                cancelText: 'Weiter bearbeiten',
                okText: 'Änderungen verwerfen',
                autoFocusButton: 'cancel',
                onOk: () => {
                    dispatch(closeDrawer(key));
                    return false;
                }
            })
        } else {
            dispatch(closeDrawer(key))
        }

    }

    const form_layout = {
        labelCol: {span: 4},
        wrapperCol: {span: 18},
    };

    const form_element = (
        <Form
            form={form}
            onFinish={onFormFinish}
            {...form_layout}
        >
            <Form.Item label="Name" style={{marginBottom: 0}}>
                <Form.Item
                    name="title"
                    style={{display: 'inline-block', width: 'calc(20% - 5px)'}}
                >
                    <Input name='title' placeholder="Title"/>
                </Form.Item>
                <Form.Item
                    name="firstname"
                    style={{display: 'inline-block', width: 'calc(40% - 6px)', margin: '0 8px'}}>
                    <Input name="firstname" placeholder="Firstname"/>
                </Form.Item>
                <Form.Item
                    style={{display: 'inline-block', width: 'calc(40% - 6px)'}}
                    name="surname">
                    <Input name="surname" placeholder="Surname"/>
                </Form.Item>
            </Form.Item>
            <Form.Item
                label="Geschlecht"
                name="gender"
            >
                <Select
                    placeholder="Geschlecht"
                    style={{width: '146px'}}
                >
                    <Select.Option key={0} value={0}>unbekannt</Select.Option>
                    <Select.Option key={1} value={1}>männlich</Select.Option>
                    <Select.Option key={2} value={2}>weiblich</Select.Option>
                    <Select.Option key={3} value={3}>divers</Select.Option>
                    <Select.Option key={9} value={9}>nicht zutreffend</Select.Option>
                </Select>
            </Form.Item>
            <Form.Item
                label="E-Mail"
                name="mail"
            >
                <Input name="mail" placeholder="E-Mail"/>
            </Form.Item>
            <Form.Item
                label="Telefon"
                name="phone"
            >
                <Input name="phone" placeholder="Telefonnummer"/>
            </Form.Item>
            <Form.Item
                label="Mobil"
                name="mobile"
            >
                <Input name="mobile" placeholder="Mobilnummer"/>
            </Form.Item>

            <Form.Item label="Notizen" name="comment">
                <TextArea allowClear name="comment" placeholder="Platz für Notizen" maxLength={2048}/>
            </Form.Item>

            <Form.Item
                label=" "
                colon={false}
            >
                <Form.Item
                    style={{display: 'inline-block'}}
                >
                    <Button type="primary" htmlType="submit">
                        <IconSet type='save'/>&nbsp;Speichern
                    </Button>
                </Form.Item>
                <Form.Item
                    style={{display: 'inline-block', margin: '0 24px'}}
                >
                    <Button onClick={onDrawerClose}>
                        <IconSet type='cancel'/>&nbsp;Änderungen verwerfen
                    </Button>
                </Form.Item>
            </Form.Item>
        </Form>
    )

    const menu = (
        isNew() ? (<></>) : (
            <Dropdown
                arrow
                overlay={
                    <Menu
                        style={{padding: 12}}
                        // @ts-ignore
                        items={[
                            {
                                key: "disable",
                                onClick: onDisable,
                                icon: <IconSet type='disable'/>,
                                label: !!object?.props?.disabled ? ("Reaktivieren") : ("Deaktivieren")
                            },
                            {
                                key: "delete",
                                danger: true,
                                disabled: !object?.props?.is_deletable,
                                onClick: onDelete,
                                icon: <IconSet type='delete'/>,
                                label: "Löschen"
                            },
                        ]}
                    />
                }
                placement="bottomRight"
                className='button-edit'
            >
                <CustomButton mode='more'/>
            </Dropdown>)
    )

    return (
        <Drawer
            open={stack.length > 0 && drawer_stack[drawer_stack.length - 1] === key}
            // @ts-ignore
            size={props.size}
            onClose={onDrawerClose}
            forceRender={true}
            title={
                <Space direction="horizontal" style={{width: '100%', justifyContent: 'center'}}>
                    <h1 style={{marginBottom: 0}}>{title + (isNew() ? " hinzufügen" : " bearbeiten")}</h1>
                </Space>
            }
            extra={
                <Space>
                    <VCFImport form={form}/>
                    {menu}
                </Space>
            }
            zIndex={drawer_stack[drawer_stack.length - 1] === key ? 1000 : 900}

        >
            {form_element}
        </Drawer>
    )
}


PersonDrawer.defaultProps = {
    size: 'large'
}